import React, { useCallback, useLayoutEffect, useRef } from 'react'

import {
  useEvent,
  useIsCameraMoving,
  useIsCameraZooming,
  usePan,
  useZoom,
  useRootNodeAbsoluteExtent,
} from '@sketch-hq/sketch-web-renderer'

import { useDevToolsSetting } from '@sketch/devtools'
import { Wrapper, OriginCross } from './OriginContent.styles'

type Pan = { x: number; y: number }
type Extent = ReturnType<typeof useRootNodeAbsoluteExtent>

const NO_PAN: Pan = { x: 0, y: 0 }

const PLACEMENT = [
  'transform',
  'width',
  'height',
  'marginTop',
  'marginLeft',
] as const

type Placement = Record<(typeof PLACEMENT)[number], string>

const placement = (
  pan: Pan,
  zoom: number,
  rootExtent: Extent | undefined
): Placement => ({
  transform: `translate3d(${Math.round(pan.x * zoom)}px, ${Math.round(
    pan.y * zoom
  )}px, 0)`,
  width: `${(rootExtent?.width ?? 0) * zoom}px`,
  height: `${(rootExtent?.height ?? 0) * zoom}px`,
  marginTop: `${-(rootExtent?.y ?? 0) * zoom}px`,
  marginLeft: `${-(rootExtent?.x ?? 0) * zoom}px`,
})

export const OriginContent: React.FCC = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null)

  const pan = usePan() || NO_PAN
  const zoom = useZoom() || 1
  const isCameraMoving = useIsCameraMoving()
  const isCameraZooming = useIsCameraZooming()

  const [showPageOrigin] = useDevToolsSetting('webRenderer.showPageOrigin')
  const rootExtent = useRootNodeAbsoluteExtent()

  const camera = useRef({ pan, zoom, rootExtent })

  /** What the element was last given, so that only changes are written. */
  const placed = useRef<Partial<Placement>>({})

  const place = useCallback(() => {
    const element = ref.current
    if (!element) return

    const { pan, zoom, rootExtent } = camera.current
    const next = placement(pan, zoom, rootExtent)

    // Only what moved. This runs inside the frame the renderer draws in, so
    // the canvas does not present until it returns: a pan changes the
    // transform alone, and writing the size again would dirty the layout of
    // every overlay inside for nothing. React wrote these as a prop, which
    // skipped the unchanged ones in the same way.
    for (const property of PLACEMENT) {
      if (next[property] === placed.current[property]) continue

      element.style[property] = next[property]
      placed.current[property] = next[property]
    }
  }, [])

  /**
   * The renderer moves the camera inside the animation frame it draws in, and
   * reports it from there - so placing the overlays here puts them in the same
   * paint as the canvas they sit on. Rendering them instead, as this did, left
   * them a frame behind: React commits in a later task, once that frame is
   * painted.
   *
   * `useEvent` subscribes to the renderer that exists when this renders, so it
   * relies on the `usePan`/`useZoom` above to re-render this once there is one.
   */
  useEvent(
    'cameraPanChange',
    useCallback(
      (x: number, y: number) => {
        camera.current.pan = { x, y }
        place()
      },
      [place]
    )
  )

  useEvent(
    'cameraZoomChange',
    useCallback(
      (zoom: number) => {
        camera.current.zoom = zoom
        place()
      },
      [place]
    )
  )

  /**
   * Context carries the same pan and zoom, a commit later. On a busy main
   * thread that commit can land after a newer event has already placed the
   * overlays, so it is not allowed to place them on its own - it would take
   * them back to where the camera no longer is, which reads worse than the
   * frame of lag it replaces.
   *
   * It places them when it is carrying something the camera events do not
   * report: the first frame, and a re-rooted scene, which moves the camera and
   * resizes the page under it. The resize arrives on its own, a debounce after
   * the re-root, so it is watched for separately.
   */
  const carriesTheCamera = useRef(true)

  useEvent(
    'sceneChange',
    useCallback(() => {
      carriesTheCamera.current = true
    }, [])
  )

  useLayoutEffect(() => {
    const resized = camera.current.rootExtent !== rootExtent
    camera.current.rootExtent = rootExtent

    if (carriesTheCamera.current) {
      carriesTheCamera.current = false
      camera.current.pan = pan
      camera.current.zoom = zoom
    } else if (!resized) {
      return
    }

    place()
  }, [pan, zoom, rootExtent, place])

  const style = {
    // willChange hints the browser that we are going to update these properties
    willChange: isCameraMoving ? 'contents' : undefined,
    // Disabling the pointer-events when the camera is moving, so the user can pan/scroll freely
    pointerEvents: isCameraMoving ? 'none' : 'auto',
  } as const

  return (
    <Wrapper
      ref={ref}
      style={style}
      data-canvas-zooming={isCameraZooming}
      data-testid="origin-content"
    >
      {children}
      {showPageOrigin && <OriginCross />}
    </Wrapper>
  )
}
